// Phase 20 · Community V3 · Events calendar (month/week/day)
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, CalendarDays } from "lucide-react";
import EventCalendarGrid from "../../../components/community_v3/EventCalendarGrid";

const API = process.env.REACT_APP_BACKEND_URL;

function toISO(d) { return new Date(d).toISOString(); }

export default function EventsCalendar() {
    const [view, setView] = useState("month");
    const [cursor, setCursor] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [disabled, setDisabled] = useState(false);

    const range = useMemo(() => {
        const c = new Date(cursor);
        let start, end;
        if (view === "month") {
            start = new Date(c.getFullYear(), c.getMonth() - 1, 1);
            end = new Date(c.getFullYear(), c.getMonth() + 2, 0, 23, 59, 59);
        } else if (view === "week") {
            start = new Date(c); start.setDate(c.getDate() - 14);
            end = new Date(c); end.setDate(c.getDate() + 14);
        } else {
            start = new Date(c); start.setHours(0, 0, 0, 0);
            end = new Date(c); end.setHours(23, 59, 59, 999);
        }
        return { start: toISO(start), end: toISO(end) };
    }, [cursor, view]);

    useEffect(() => {
        (async () => {
            const params = new URLSearchParams({ start: range.start, end: range.end });
            try {
                const r = await fetch(`${API}/api/community/events/calendar?${params}`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); setEvents([]); return; }
                if (r.ok) { const d = await r.json(); setEvents(d.items || []); }
            } catch {}
        })();
    }, [range]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="events-calendar-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/events" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Events
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <Calendar size={16} className="text-[#FFFFFF]" /> Community calendar
                    </h1>
                    <div className="ml-auto inline-flex items-center gap-1 bg-[#111111] border border-[#1A1A1A] rounded-lg overflow-hidden">
                        {["month", "week", "day"].map((v) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setView(v)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-widest capitalize ${view === v ? "bg-[#FFFFFF]/20 text-[#FFFFFF]" : "text-neutral-400 hover:text-neutral-200"}`}
                                data-testid={`cal-view-${v}`}
                            >
                                {v}
                            </button>
                        ))}
                    </div>
                </div>
            </header>
            <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA]">
                        Events engine disabled.
                    </div>
                )}
                <EventCalendarGrid view={view} cursor={cursor} onCursorChange={setCursor} events={events} />
                <p className="text-[10px] text-neutral-500 flex items-center gap-1 justify-center">
                    <CalendarDays size={11} /> {events.length} events in view
                </p>
            </main>
        </div>
    );
}
